import Vue, { CreateElement } from '@src/vue'
import { Component } from 'vue-property-decorator'

import { Select } from '@src/elements/select/select'
import { Option } from '@src/elements/select/option'
import { Input } from '@src/elements/input/input'
import { Checkbox } from '@src/elements/input/checkbox'
import { Form } from '@src/components/form/form'
import { Button } from '@src/elements/button/button'
import { Logo } from '@src/elements/logo/logo'
import { DatePicker } from '@src/elements/input/date-picker'
import ApiClient from '@src/lib/api_client'
import { Icon } from '@src/elements/icon/icon'
import { Spacer } from '@src/elements/spacer/spacer'
import { Subtitle1, Subtitle2 } from '@src/elements/typography/typography'
import {
  isEmailValid,
  isUsernameValid,
  isFieldFilled,
  validatePassword
} from '@src/util/validation'
import Member from '@src/data/member'
import { highlightFulfilledRequirements, snakecaseKeys, track, getGenderName } from '@src/util/functions'
import { API } from '@src/lib/api_path'
import { ContentBox } from '@src/elements/content-box/content-box'
import { InputFootnote } from '@src/elements/input/input-footnote'
import { TermsAndConditions } from '@src/routes/members/terms-conditions'
import { PasswordRequirements } from '@src/components/password-requirements/password-requirements'
import { convertToYYYYMMDD } from '@src/util/date-format'
import { getTomorrowDate } from '@src/util/date-utilities'
import { ActionBar } from '@src/components/action-bar/action-bar'
import { Breadcrumbs } from '@src/elements/breadcrumbs/breadcrumbs'
import { defaultMemberFilters } from '@src/state/dafaults'

import './new-member.sass'
import SessionManager from '@src/lib/session_manager'

class ErrorResponse {
  errors: {
    base: string
    username: string[]
  }

  constructor (attributes?) {
    Object.assign(this, attributes)
  }
}

@Component
export class NewMember extends Vue {

  protected member: Member = new Member({
    code: '',
    firstName: '',
    lastName: '',
    gender: 'f',
    username: '',
    displayName: '',
    email: '',
    birthdate: '',
    clubRoleType: 'member',
    scheduledDeactivationDate: null
  })

  protected scheduledDeactivationEnabled: boolean = false

  protected apiClient: ApiClient = new ApiClient()

  protected errorCodes: string[] = []
  protected touchedFields: Set<string> = new Set()

  protected createdMemberId: number | null = null
  protected termsReacceptanceRequired: boolean = false

  private highlightRequirements = {
    minLength: false,
    oneLetter: false,
    oneNumber: false,
    sameAsConfirm: false
  }
  private newPassword: string = ''
  private confirmPassword: string = ''

  constructor () {
    super()
    track('/members/create')
  }

  updated () {
    document.title = `${this.$root.$t('PIXFORMANCE_PLATFORM')} - ${this.$root.$t('MEMBERS')} - ${this.$root.$t('PAGE.MEMBERS.LIST.CREATE_MEMBER')} - ${this.$root.$t('CREATE')}`
  }

  scrollError () {
    let container = document.querySelector('.scroll-content')
    if (container) {
      let firstErrorInput = container.querySelector('.component-form')
      if (firstErrorInput) {
        container.scrollTop = firstErrorInput.getBoundingClientRect().top
      }
    }
  }

  async attemptCreateMember () {
    // Mark all fields as touched for final validation
    this.touchedFields.add('code')
    this.touchedFields.add('first_name')
    this.touchedFields.add('last_name')
    this.touchedFields.add('username')
    this.touchedFields.add('displayName')
    this.touchedFields.add('email')
    this.touchedFields.add('newPassword')
    this.touchedFields.add('confirmPassword')

    // Autofill username with QR code only if username is still empty at submit time
    this.autofillUsername()

    this.validateForm()

    if (this.errorCodes.length === 0) {

      await this.createMember()
      return this.setPassword()
    }
    this.scrollError()
  }

  createMember () {
    const member = new Member(this.member)
    if (member.birthdate) {
      member.birthdate = convertToYYYYMMDD(member.birthdate)
    }
    if (this.scheduledDeactivationEnabled && member.scheduledDeactivationDate) {
      member.scheduledDeactivationDate = convertToYYYYMMDD(member.scheduledDeactivationDate)
    } else {
      member.scheduledDeactivationDate = null
    }
    if (!member.email) {
      member.email = null
    }
    if (!isFieldFilled(member.code)) {
      delete (member as any).code
    }

    return this.apiClient.post(API.TRAINERS_API.MEMBERS, { member: snakecaseKeys(member) })
      .then((response: any) => {
        this.createdMemberId = response.member.id
        this.$store.dispatch('filterMembers', defaultMemberFilters())
        this.termsReacceptanceRequired = response.member.terms_reacceptance_required
      }).catch(error => {
        this.setErrors(error.status, new ErrorResponse(error.errorData))
        this.scrollError()
      })
  }

  setPassword () {
    if (this.createdMemberId) {
      return this.apiClient.put(API.TRAINERS_API.MEMBER_DETAILS.replace('{memberId}', `${this.createdMemberId}`), { member: { password: this.newPassword } })
    }
  }

  acceptTermsCallback () {
    this.closeCreateMember()
    this.$store.dispatch('showToast', {
      message: `${this.member.firstName} ${this.$t('PAGE.MEMBERS.LIST.CREATE_MEMBER_NOTIFICATION')}`,
      fadeOut: false,
      actionText: this.$t('PAGE.MEMBERS.ACTIVE_NOW'),
      actionCallback: () => {
        if (this.createdMemberId) {
          this.goToMemberStatus(this.createdMemberId)
        }
      }
    })
  }

  goToMemberStatus (memberId: number) {
    this.$router.push(`/trainers/platform/members/${memberId}/status`)
  }

  setErrors (statusCode, errorResponse) {
    switch (true) {
      case statusCode === 409:
        this.errorCodes.push('code')
        break
      case statusCode === 422 && errorResponse.errors && errorResponse.errors.base && errorResponse.errors.base.includes('email IS_TAKEN'):
        this.errorCodes.push('email_duplicate')
        break
      case statusCode === 422 && errorResponse.errors && errorResponse.errors.email && errorResponse.errors.email.length > 0 :
        this.errorCodes.push('email')
        break
      case statusCode === 422 && errorResponse.errors && errorResponse.errors.username && errorResponse.errors.username.length > 0 :
        this.errorCodes.push('username_duplicate')
        break
      case statusCode === 422 && errorResponse.errors && errorResponse.errors.base && errorResponse.errors.base.includes('username'):
        this.errorCodes.push('username')
        break
    }
  }

  closeCreateMember () {
    this.$router.push('/trainers/platform/members')
  }

  setNewPassword (value: string) {
    this.newPassword = value
    this.touchedFields.add('newPassword')
    this.touchedFields.add('confirmPassword')

    this.clearFieldErrors(['newPassword', 'confirmPassword'])
    this.highlightRequirements = highlightFulfilledRequirements(this.newPassword, this.confirmPassword)
    this.validateForm(['newPassword', 'confirmPassword'])
  }

  setConfirmPassword (value: string) {
    this.confirmPassword = value
    this.touchedFields.add('confirmPassword')
    this.touchedFields.add('newPassword')

    this.clearFieldErrors(['newPassword', 'confirmPassword'])
    this.highlightRequirements = highlightFulfilledRequirements(this.newPassword, this.confirmPassword)
    this.validateForm(['newPassword', 'confirmPassword'])
  }

  goToNewUser () {
    this.$router.push('/trainers/platform/members')
  }

  setScheduledDeactivationEnabled (value: boolean) {
    this.scheduledDeactivationEnabled = value

    if (!value) {
      this.member.scheduledDeactivationDate = null
      this.clearFieldErrors(['scheduledDeactivationDate'])
    }
  }

  render (h: CreateElement) {
    return <div class='create-member scroll-content'>

      <ActionBar>
        <Breadcrumbs clickAction={this.goToNewUser} crumbs={[
          <div>{this.$t('USERS')}</div>
        ]} />
        <Subtitle2>{this.$t('PAGE.MEMBERS.LIST.CREATE_MEMBER_ACTION')}</Subtitle2>
      </ActionBar>

      {this.termsReacceptanceRequired ? <TermsAndConditions acceptCallback={this.acceptTermsCallback} memberId={this.createdMemberId} />

        : <div class='create-member-wrapper'>
          <div class='row create-header'>
            <div class='qr-section'>
              <Logo />
              <Spacer />
              <Icon class='qr-icon' name={'util/qr-code'} />
            </div>
          </div>

          <ContentBox>
            <Form direction={'column'} slot={'content'}>
              <div class='row'>
                <div>
                  <Input ref='code' label={this.$t('PAGE.MEMBERS.CREATE.QR_CODE.TITLE')}
                         class={this.hasError('code') ? 'error' : null} value={this.member.code}
                         valueChanged={(value: string) => { this.member.code = value; this.clearFieldErrors(['code']) }} />
                  <InputFootnote class='error'>{this.hasError('code') ? this.$t('PAGE.MEMBERS.CREATE.ERROR.INVALID_QR_CODE.MESSAGE') : null}</InputFootnote>
                </div>
              </div>

              <Subtitle1 class='member-details'>{this.$t('PAGE.MEMBERS.DETAILS_TITLE')}</Subtitle1>

              <div class='row'>
                <div>
                  <Input ref='firstname' class={this.hasError('first_name') ? 'error' : null} value={this.member.firstName}
                         valueChanged={(value: string) => { this.member.firstName = value; this.touchedFields.add('first_name'); this.clearFieldErrors(['first_name']); this.validateForm(['first_name']) }} label={`${this.$t('FIRST_NAME')}*`} />
                  <InputFootnote
                    class='error'>{this.hasError('first_name') ? this.$t('VALIDATION.MESSAGES.EMPTY.MESSAGE', { fieldName: this.$t('FIRST_NAME') }) : null}</InputFootnote>
                </div>

                <div>
                  <Input ref='lastname' class={this.hasError('last_name') ? 'error' : null} value={this.member.lastName}
                         valueChanged={(value: string) => { this.member.lastName = value; this.touchedFields.add('last_name'); this.clearFieldErrors(['last_name']); this.validateForm(['last_name']) }} label={`${this.$t('LAST_NAME')}*`} />
                  <InputFootnote
                    class='error'>{this.hasError('last_name') ? this.$t('VALIDATION.MESSAGES.EMPTY.MESSAGE', { fieldName: this.$t('LAST_NAME') }) : null}</InputFootnote>
                </div>
              </div>

              <div class='row'>
                <div>
                  <Input ref='username' class={this.errorCodes.find((u) => u.includes('username')) ? 'error' : ''} value={this.member.username}
                         valueChanged={(value: string) => { this.member.username = value.replace(/\s+/g, ''); this.touchedFields.add('username'); this.clearFieldErrors(['username_required','username_too_short','username','username_duplicate']); this.validateForm(['username']) }} label={this.$t('USERNAME')} />
                  <InputFootnote>&#8226; {this.$t('PAGE.MEMBERS.CREATE.USERNAME_AUTOFILL_HINT') || 'Will be auto-filled with QR code if empty'}</InputFootnote>
                  <InputFootnote class='error'>{this.hasError('username_duplicate') ? this.$t('PAGE.MEMBERS.CREATE.ERROR.DUPLICATE_USERNAME') : null}</InputFootnote>
                  {this.hasError('username_too_short') && this.member.username ? <InputFootnote class='error'>&#8226; {this.$t('PAGE.MEMBERS.ACCOUNT.USERNAME_TOO_SHORT')}</InputFootnote> : ''}
                  {this.hasError('username') && this.member.username ? <InputFootnote class='error'>&#8226; {this.$t('PAGE.MEMBERS.ACCOUNT.USERNAME_CHARACTERS_ALLOWED')}</InputFootnote> : '' }
                  {!this.hasError('username_too_short') && this.touchedFields.has('username') && this.member.username ? <InputFootnote>&#8226; {this.$t('PAGE.MEMBERS.ACCOUNT.USERNAME_TOO_SHORT')}</InputFootnote> : ''}
                  {!this.hasError('username') && this.touchedFields.has('username') && this.member.username ? <InputFootnote>&#8226; {this.$t('PAGE.MEMBERS.ACCOUNT.USERNAME_CHARACTERS_ALLOWED')}</InputFootnote> : ''}
                </div>
              </div>

              <div class='row'>
                <div>
                  <Input ref='displayName' class={this.hasError('displayName') || this.hasError('displayName_too_short') ? 'error' : null} value={this.member.displayName}
                         valueChanged={(value: string) => { this.member.displayName = value; this.touchedFields.add('displayName'); this.clearFieldErrors(['displayName', 'displayName_too_short']); this.validateForm(['displayName']) }} label={this.$t('PAGE.MEMBERS.ACCOUNT.DISPLAY_NAME')} />
                  <InputFootnote>&#8226; {this.$t('PAGE.MEMBERS.CREATE.DISPLAYNAME_AUTOFILL_HINT') || 'Will be auto-filled with QR code if empty'}</InputFootnote>
                  <InputFootnote class='error'>{this.hasError('displayName_too_short') && this.member.displayName ? this.$t('PAGE.MEMBERS.ACCOUNT.DISPLAY_NAME_TOO_SHORT') : null}</InputFootnote>
                </div>
              </div>

              <div class='row'>
                <div>
                  <Input ref='email' class={this.hasError('email') || this.hasError('email_duplicate') ? 'error' : null} value={this.member.email}
                         valueChanged={(value: string) => { this.member.email = value; this.touchedFields.add('email'); this.clearFieldErrors(['email','email_duplicate']); this.validateForm(['email']) }} label={this.$t('EMAIL')} />
                  <InputFootnote class='error'>{this.hasError('email') ? this.$t('PAGE.MEMBERS.CREATE.ERROR.INVALID_EMAIL.MESSAGE') : null}</InputFootnote>
                  <InputFootnote class='error'>{this.hasError('email_duplicate') ? this.$t('PAGE.MEMBERS.CREATE.ERROR.DUPLICATE_EMAIL.MESSAGE') : null}</InputFootnote>
                </div>
              </div>

              <div class='row'>
                <div>
                  <Input ref='newPassword' type='password' class={this.hasError('newPassword') ? 'error' : null} value={this.newPassword} valueChanged={this.setNewPassword}
                         label={`${this.$t('PAGE.MEMBERS.CHANGE_PASSWORD.NEW')}*`} />
                </div>
                <div>
                  <Input ref='confirmPassword' type='password' class={`short ${this.hasError('confirmPassword') ? 'error' : null}`} value={this.confirmPassword}
                         valueChanged={this.setConfirmPassword} label={`${this.$t('PAGE.MEMBERS.CHANGE_PASSWORD.CONFIRM')}*`} />
                </div>
              </div>
              <PasswordRequirements
                highlightRequirements={this.highlightRequirements}
                class={(this.hasError('newPassword') || this.hasError('confirmPassword')) ? 'error' : (this.newPassword && this.confirmPassword && validatePassword(this.newPassword) && this.newPassword === this.confirmPassword ? 'valid' : null)}
              />
              <Spacer />

              <div class='row'>
                <div>
                  <Select ref='gender' label={`${this.$t('PAGE.MEMBERS.GENDER.TITLE')}*`}
                          valueChanged={(value: string) => this.member.gender = value}
                          value={this.$t(getGenderName(this.member.gender))}>
                    <Option type='single-option' value={'f'} label={this.$t('PAGE.MEMBERS.GENDER.FEMALE')} selected={this.member.gender === 'f'} />
                    <Option type='single-option' value={'m'} label={this.$t('PAGE.MEMBERS.GENDER.MALE')} selected={this.member.gender === 'm'} />
                    <Option type='single-option' value={'d'} label={this.$t('PAGE.MEMBERS.GENDER.DIVERSE')} selected={this.member.gender === 'd'} />
                  </Select>
                </div>

                <div>
                  <DatePicker ref='birthdate' type='date' value={this.member.birthdate}
                              change={(value: string) => this.member.birthdate = value} allowFutureDate={false} label={this.$t('BIRTHDATE')} />
                </div>

            </div>
            <div className='row'>
                <Select ref='role' label={`${this.$t('PAGE.MEMBERS.ROLE.SELECT')}*`}
                        valueChanged={(value: string) => this.member.clubRoleType = value}
                        value={this.$t(`PAGE.MEMBERS.ROLE.${this.member.clubRoleType.toUpperCase() || 'MEMBER'}`)}>
                  <Option type='single-option' value={'member'} label={this.$t('PAGE.MEMBERS.ROLE.MEMBER')} selected={true} />
                  {SessionManager.Instance.isOwner() && <Option type='single-option' value={'trainer'} label={this.$t('PAGE.MEMBERS.ROLE.TRAINER')} selected={this.member.clubRoleType === 'trainer'} />}
                  {SessionManager.Instance.isOwner() && <Option type='single-option' value={'therapist'} label={this.$t('PAGE.MEMBERS.ROLE.THERAPIST')} selected={this.member.clubRoleType === 'therapist'} />}
                  {SessionManager.Instance.isOwner() && <Option type='single-option' value={'assistant'} label={this.$t('PAGE.MEMBERS.ROLE.ASSISTANT')} selected={this.member.clubRoleType === 'assistant'} />}
                </Select>
            </div>

            <Subtitle1 class='member-details scheduled-deactivation-title'>{this.$t('PAGE.MEMBERS.SCHEDULED-DEACTIVATION')}</Subtitle1>

            <div class='row scheduled-deactivation-controls'>
              <div class='scheduled-deactivation-checkbox'>
                <Checkbox value={this.scheduledDeactivationEnabled}
                          change={this.setScheduledDeactivationEnabled}
                          text={this.$t('PAGE.MEMBERS.ENABLE-SCHEDULED-DEACTIVATION')} />
                {this.scheduledDeactivationEnabled && this.member.scheduledDeactivationDate &&
                  <div class='scheduled-deactivation-message'>
                    {`${this.$t('PAGE.MEMBERS.SCHEDULED-DEACTIVATION-DATE')} ${this.member.scheduledDeactivationDate}`}
                  </div>
                }
              </div>

              <div class='date-pickers'>
                <DatePicker ref='scheduledDeactivationDate' type='date' value={this.member.scheduledDeactivationDate}
                            change={(value: string) => {
                              this.member.scheduledDeactivationDate = value
                              this.clearFieldErrors(['scheduledDeactivationDate'])
                            }}
                            allowPastDate={false}
                            firstAvailableDate={getTomorrowDate()}
                            class={this.hasError('scheduledDeactivationDate') ? 'error' : null}
                            disabled={!this.scheduledDeactivationEnabled}
                            popper-class={'over-modal'}
                            label={this.$t('DATE')} />
                <InputFootnote class='error'>
                  {this.hasError('scheduledDeactivationDate') ? this.$t('PAGE.MEMBERS.PLEASE-ENTER-DEACTIVATION-DATE-ERROR') : null}
                </InputFootnote>
              </div>
            </div>
            </Form>

            <div slot={'bottom'}>
              <Button ref='cancel-button' class='cancel-button' label={this.$t('PROMPTS.CANCEL')} click={this.closeCreateMember} type='outline' />
              <Button ref='save-button' class='save-button' label={this.$t('NEXT')} click={this.attemptCreateMember} type='elevated' />
            </div>
          </ContentBox>
        </div>
      }
    </div>
  }

  protected validateForm (specificFields?: string[]) {
    let errorCodes: string[] = []

    const shouldValidate = (fieldName: string) => {
      return specificFields ? specificFields.includes(fieldName) : this.touchedFields.has(fieldName)
    }

    if (shouldValidate('first_name')) {
      if (!isFieldFilled(this.member.firstName)) {
        errorCodes.push('first_name')
      }
    }

    if (shouldValidate('last_name')) {
      if (!isFieldFilled(this.member.lastName)) {
        errorCodes.push('last_name')
      }
    }

    if (shouldValidate('email')) {
      if (isFieldFilled(this.member.email)) {
        if (!isEmailValid(this.member.email!)) {
          errorCodes.push('email')
        }
      }
    }

    if (shouldValidate('newPassword')) {
      if (!validatePassword(this.newPassword)) {
        errorCodes.push('newPassword')
      }
    }

    if (shouldValidate('confirmPassword')) {
      if (this.newPassword !== this.confirmPassword) {
        errorCodes.push('confirmPassword')
      }
    }

    if (shouldValidate('username')) {
      // Username is now optional, only validate if filled
      if (isFieldFilled(this.member.username)) {
        if (this.member.username.length < 5) {
          errorCodes.push('username_too_short')
        }
        if (!isUsernameValid(this.member.username)) {
          errorCodes.push('username')
        }
      }
    }

    if (shouldValidate('displayName')) {
      // Display name is now optional, only validate if filled
      if (isFieldFilled(this.member.displayName)) {
        if (this.member.displayName.trim().length < 2) {
          errorCodes.push('displayName_too_short')
        }
      }
    }

    if (this.scheduledDeactivationEnabled && !isFieldFilled(this.member.scheduledDeactivationDate)) {
      errorCodes.push('scheduledDeactivationDate')
    }

    if (specificFields || this.touchedFields.size > 0) {
      const fieldsValidated = specificFields || Array.from(this.touchedFields)
      this.errorCodes = this.errorCodes.filter(code => {
        return !this.isErrorForField(code) || fieldsValidated.some(field => this.errorBelongsToField(code, field))
      })
      errorCodes.forEach(code => {
        if (!this.errorCodes.includes(code)) {
          this.errorCodes.push(code)
        }
      })
    } else {
      this.errorCodes = errorCodes
    }
  }

  protected clearFieldErrors (fields: string[]) {
    if (!this.errorCodes || this.errorCodes.length === 0) return
    this.errorCodes = this.errorCodes.filter((c) => !fields.includes(c))
  }

  protected autofillUsername () {
    // autocomplete username with the qr code if empty
    if (!isFieldFilled(this.member.username)) {
      this.member.username = this.member.code || ''
    }
    // autocomplete displayName with the qr code if empty
    if (!isFieldFilled(this.member.displayName)) {
      this.member.displayName = this.member.code || ''
    }
  }

  protected hasError (fieldName: string): boolean {
    return !!this.errorCodes.find(err => fieldName === err)
  }

  private isErrorForField (errorCode: string): boolean {
    const fieldErrors = [
      'code', 'first_name', 'last_name', 'email', 'email_duplicate',
      'username', 'username_required', 'username_too_short', 'username_duplicate',
      'displayName', 'displayName_too_short', 'newPassword', 'confirmPassword',
      'scheduledDeactivationDate'
    ]
    return fieldErrors.includes(errorCode)
  }

  private errorBelongsToField (errorCode: string, fieldName: string): boolean {
    const errorFieldMap: { [key: string]: string[] } = {
      'code': ['code'],
      'first_name': ['first_name'],
      'last_name': ['last_name'],
      'email': ['email', 'email_duplicate'],
      'username': ['username', 'username_required', 'username_too_short', 'username_duplicate'],
      'displayName': ['displayName', 'displayName_too_short'],
      'newPassword': ['newPassword'],
      'confirmPassword': ['confirmPassword'],
      'scheduledDeactivationDate': ['scheduledDeactivationDate']
    }
    return errorFieldMap[fieldName] ? errorFieldMap[fieldName].includes(errorCode) : false
  }
}
